import { useState, useEffect } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal/DeleteConfirmationModal.component';
import { PersonSearch } from '@/components/general/PersonSearch';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import candidatesServices from '@/services/candidates';
import skillsService from '@/services/skills';
import { FetchApiIds, TechStackType, CandidateTechStacksType } from '@/types';
import * as Styles from './AddChallengeReviewerForm.styles';
import {
  checkShouldShowConfirmationModal,
  getNewSkillMock,
} from './AddChallengeReviewerForm.utils';
import { EditReviewerData } from '../ChallengeReviewers.container';

interface AddChallengeReviewerProps {
  isOpened: boolean;
  onClose: () => void;
  onAddReviewer: (candidateId: string, skills: CandidateTechStacksType[]) => void;
  reviewerToEdit?: EditReviewerData;
}

const AddChallengeReviewerForm = ({
  isOpened,
  onClose,
  onAddReviewer,
  reviewerToEdit,
}: AddChallengeReviewerProps): JSX.Element => {
  const [selectedCandidate, setSelectedCandidate] = useState<string>('');
  const [selectedSkills, setSelectedSkills] = useState<CandidateTechStacksType[]>([]);
  const [selectedPerson, setSelectedPerson] = useState<any>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const toast = useToastContext();

  const isEditMode = !!reviewerToEdit;

  // Skills API call
  const { data: allSkillsResponse, isLoading: skillsLoading } = useApiFetch(
    [FetchApiIds.ALL_TECHS],
    candidatesServices.getAllTechsStacks
  );

  // API mutation for updating skills
  const updateSkillsMutation = useApiSubmit<
    any,
    {
      personId: string;
      skills: Array<{
        skillId: string;
        willingToReview: boolean;
      }>;
    }
  >(skillsService.updateAllPersonSkills);

  const allSkills = allSkillsResponse || [];

  // Helper function to get available skills for a specific row
  const getAvailableSkillsForRow = (currentRowSkillId: string): TechStackType[] => {
    const selectedSkillsIdsSet = new Set(selectedSkills.map((skill) => skill.skill.id));

    return allSkills.filter((skill) => {
      const isCurrentDisplayedSkill = skill.id === currentRowSkillId;
      const isAlreadySelected = selectedSkillsIdsSet.has(skill.id);
      return isCurrentDisplayedSkill || (!isAlreadySelected && skill.isActive);
    });
  };

  // Initialize form when editing
  useEffect(() => {
    if (isEditMode) {
      setSelectedCandidate(reviewerToEdit.candidateId);
      setSelectedPerson({
        id: reviewerToEdit.candidateId,
        firstName: reviewerToEdit.candidateName.split(' ')[0],
        lastName: reviewerToEdit.candidateName.split(' ').slice(1).join(' '),
      });
      setSelectedSkills(reviewerToEdit.skills);
    }
  }, [isEditMode, reviewerToEdit]);

  const handlePersonSelect = (person: any): void => {
    setSelectedPerson(person);
    setSelectedCandidate(person.id);
  };

  const handleAddSkill = (): void => {
    setSelectedSkills((prev) => [...prev, getNewSkillMock()]);
  };

  const handleRemoveSkill = (skillId: string): void => {
    setSelectedSkills((prev) => prev.filter((skill) => skill.id !== skillId));
  };

  const handleSkillChange = (skillId: string, selectedSkill: TechStackType): void => {
    setSelectedSkills((prev) =>
      prev.map((skill) => (skill.id === skillId ? { ...skill, skill: selectedSkill } : skill))
    );
  };

  const handleWillingToReviewToggle = (skillId: string): void => {
    setSelectedSkills((prev) =>
      prev.map((skill) =>
        skill.id === skillId ? { ...skill, willingToReview: !skill.willingToReview } : skill
      )
    );
  };

  const resetForm = (): void => {
    setSelectedCandidate('');
    setSelectedSkills([]);
    setSelectedPerson(null);
  };

  const handleSubmit = async (): Promise<void> => {
    if (!selectedCandidate) {
      return;
    }

    const validSkills = selectedSkills.filter((skill) => skill.skill.id);

    if (isEditMode) {
      try {
        const skillsForAPI = validSkills.map((skill) => ({
          skillId: skill.skill.id,
          willingToReview: skill.willingToReview ?? true,
        }));

        await updateSkillsMutation.mutateAsync({
          personId: selectedCandidate,
          skills: skillsForAPI,
        });

        onAddReviewer(selectedCandidate, validSkills);
      } catch (error) {
        toast.showErrorFeedback(
          'There was a problem updating the reviewer skills, please try again later.'
        );
      }
    } else {
      onAddReviewer(selectedCandidate, validSkills);
    }
    setShowConfirmationModal(false);
    resetForm();
    onClose();
  };

  const onClickSubmit = (): void => {
    const shouldShowConfirmationModal = checkShouldShowConfirmationModal({
      reviewerToEdit,
      selectedSkills,
      isEditMode,
      showConfirmationModal,
    });

    if (shouldShowConfirmationModal) {
      setShowConfirmationModal(true);
      return;
    }

    handleSubmit();
  };

  const handleCancel = (): void => {
    resetForm();
    onClose();
  };

  const title = isEditMode ? 'Edit Challenge Reviewer' : 'Add New Challenge Reviewer';

  return (
    <>
      <Styles.AddReviewerForm
        data-testid="add-reviewer-form"
        id="add-reviewer-form"
        isOpened={isOpened}
      >
        <Styles.FormContainer>
          <Styles.FormHeader>
            <h3>{title}</h3>
          </Styles.FormHeader>

          <Styles.FormContent>
            <Styles.FormField>
              <span>Select Candidate</span>
              {!isEditMode ? (
                <PersonSearch
                  placeholder="Search for a candidate..."
                  onPersonSelect={handlePersonSelect}
                />
              ) : (
                <div
                  style={{
                    padding: '8px 12px',
                    background: '#f5f5f5',
                    borderRadius: '4px',
                    color: '#666',
                  }}
                >
                  {selectedPerson
                    ? `${selectedPerson.firstName} ${selectedPerson.lastName}`
                    : 'No candidate selected'}
                </div>
              )}
            </Styles.FormField>

            <Styles.FormField hasScroll>
              <span>Skills</span>

              {!isEditMode && (
                <Styles.DisabledMessage>
                  Skills can only be edited after the reviewer is created. Please create the
                  reviewer first, then edit their skills.
                </Styles.DisabledMessage>
              )}

              {isEditMode && (
                <>
                  {skillsLoading ? (
                    <div>Loading skills...</div>
                  ) : (
                    <Styles.SkillsContainer>
                      <Styles.SkillsList>
                        {selectedSkills.map((skillRow) => (
                          <Styles.SkillRow key={skillRow.id}>
                            <Styles.SkillRowTop>
                              <Styles.SkillSelect
                                value={skillRow.skill.id}
                                onChange={(e) => {
                                  const selectedSkill = allSkills.find(
                                    (s) => s.id === e.target.value
                                  );
                                  if (selectedSkill) {
                                    handleSkillChange(skillRow.id, selectedSkill);
                                  }
                                }}
                              >
                                <option value="">Select a skill</option>
                                {getAvailableSkillsForRow(skillRow.skill.id).map((skill) => (
                                  <option key={skill.id} value={skill.id}>
                                    {skill.name}
                                  </option>
                                ))}
                              </Styles.SkillSelect>

                              <Styles.RemoveSkillButton
                                onClick={() => handleRemoveSkill(skillRow.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </Styles.RemoveSkillButton>
                            </Styles.SkillRowTop>

                            <Styles.SkillRowBottom>
                              <Styles.WillingToReviewToggle
                                type="checkbox"
                                checked={skillRow.willingToReview}
                                onChange={() => handleWillingToReviewToggle(skillRow.id)}
                              />

                              <span style={{ fontSize: '12px', color: '#718096' }}>
                                Willing to review
                              </span>
                            </Styles.SkillRowBottom>
                          </Styles.SkillRow>
                        ))}
                      </Styles.SkillsList>

                      <Styles.AddSkillButton
                        onClick={handleAddSkill}
                        variantColor="blue"
                        variantStyle="outline"
                        size="small"
                      >
                        + Add Skill
                      </Styles.AddSkillButton>
                    </Styles.SkillsContainer>
                  )}
                </>
              )}
            </Styles.FormField>
          </Styles.FormContent>

          <Styles.FormFooter>
            <Styles.SubmitButton onClick={onClickSubmit} disabled={updateSkillsMutation.isLoading}>
              {isEditMode ? 'Update Reviewer' : 'Add Reviewer'}
              {updateSkillsMutation.isLoading && '...'}
            </Styles.SubmitButton>
            <Styles.CancelButton onClick={handleCancel}>Cancel</Styles.CancelButton>
          </Styles.FormFooter>
        </Styles.FormContainer>
      </Styles.AddReviewerForm>

      <DeleteConfirmationModal
        onConfirmHandler={handleSubmit}
        onClose={() => setShowConfirmationModal(false)}
        isOpen={showConfirmationModal}
        isButtonDisabled={updateSkillsMutation.isLoading}
        onConfirmLabel="Yes, continue"
        bodyMessage="Skills removed from a user will also be completely removed from the user's profile. Are you sure you want to continue?"
      />
    </>
  );
};

export default AddChallengeReviewerForm;
